package dev.lingvo.site.pages.app.projects.slug.data.jobs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.MtJobDto
import dev.lingvo.shared.MtJobState
import dev.lingvo.shared.Role
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LChip
import dev.lingvo.site.ds.LCopyField
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LKeyValueRow
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LPauseIcon
import dev.lingvo.site.ds.LRefreshIcon
import dev.lingvo.site.ds.LSparkleIcon
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.LSkeletonShape
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.WorkspaceSelection
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectAction
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** §10: a running receipt refreshes every 3 s and stops on any terminal state. */
private const val POLL_INTERVAL_MS = 3_000L

private const val SUPPORT_EMAIL = "support@lingvo.dev"

/**
 * The receipt for one machine-translation run.
 *
 * **`refundedAt` is the source of truth for "was this given back", never the job state.** A job
 * killed along with the process stays RUNNING for ever while the reaper has already refunded it — an
 * ordinary event on a host that suspends when idle and rolls on every deploy. A page that read the
 * state would show a spinner that never ends beside credits the workspace already has back.
 *
 * **No sentence here names an amount the server did not send.** The page used to print
 * `reservedCredits` under the label "Given back" whenever `refunded_at` was set, and to say in the
 * failure alert that "the whole reservation was returned". Both are false on the reaper's path,
 * which refunds only the undelivered proportion (`Cron.reapMtJobs`) — a run that delivered 71% was
 * telling its reader 936 came back when 270 did. `MtJobDto.refundedCredits` (V46) closes that: the
 * reaper writes what it released, in the transaction that released it, and this page reads it. Where
 * it is null — a job refunded before the column existed — the page still says *that* credits came
 * back and never how many, because a receipt that overstates a refund is worse than an incomplete one.
 *
 * **`MtJobDto.error` is never rendered** (`run-receipt.md` §3.4, §14.2). It is a raw provider blob
 * and a provider error body can echo the key that was submitted, so every failure sentence on this
 * page is fixed, client-owned copy.
 */
@Page("/app/projects/{slug}/data/jobs/{jobId}")
@Layout(".components.layouts.AppLayout")
@Composable
fun JobReceiptPage() {
    val ctx = rememberPageContext()
    JobReceiptContent(
        slug = ctx.route.params["slug"].orEmpty(),
        jobId = ctx.route.params["jobId"].orEmpty(),
    )
}

/**
 * The receipt below the route, with the fetch as a seam.
 *
 * Kobweb's `@Page` refuses parameters, so the page reads the slug and the job off the route and
 * hands the whole screen to this. [loadJob] defaults to the real transport; a test supplies its own
 * to watch what a *second* answer does to a receipt somebody is reading — which is the only place
 * the refresh behaviour is observable.
 */
@Composable
internal fun JobReceiptContent(
    slug: String,
    jobId: String,
    loadJob: suspend (String, String) -> MtJobDto = { projectSlug, job -> ApiClient.mtJob(projectSlug, job) },
) {
    val ctx = rememberPageContext()
    var refreshKey by remember { mutableStateOf(0) }
    // The slug and the job identify the receipt and still blank when they change; the refresh
    // counter only re-runs the request. As a key it discarded the figures before the request was
    // sent, so pressing Refresh replaced the receipt with a skeleton — including a lookalike
    // disabled Refresh button — and destroyed the button that had just been pressed. The poll state
    // below deliberately still resets on the counter; only the fetched value stays up.
    // **The whole `Reloadable`, not only its state.** Taking `.state` threw away the two things a
    // reader learns a refresh from: `refreshing`, which is what puts the control that started it —
    // and only that control — into a busy state, and `refreshError`, which is the only place a
    // failed refresh could be said at all. Without them "Refresh this receipt" changed nothing on
    // press and said nothing on failure, so somebody reconciling these figures against an invoice
    // believed they were looking at fresh ones when they were looking at the ones from page load.
    val receipt = loadReloadable(slug, jobId, reloadToken = refreshKey) { loadJob(slug, jobId) }
    val state = receipt.state

    // The poll's own snapshot, kept beside the loaded one rather than replacing it: when the network
    // goes away mid-run the page must keep rendering the last figures it had. Blanking a receipt
    // because a refresh failed loses numbers a person may be part-way through reconciling.
    // **Keyed on the subject, not on the refresh counter**, which is the half of the migration to
    // `loadReloadable` that had to move with it. `job` below reads `polled ?: state.value`. Once
    // `state` keeps its value across a refresh, clearing `polled` on the counter made pressing
    // Refresh fall back to the figures from page load — visibly rewinding a receipt somebody may be
    // part-way through reconciling, for as long as the round trip takes.
    var polled by remember(slug, jobId) { mutableStateOf<MtJobDto?>(null) }
    var lastUpdated by remember(slug, jobId, refreshKey) { mutableStateOf<String?>(null) }
    var reconnecting by remember(slug, jobId, refreshKey) { mutableStateOf(false) }

    val loaded = (state as? Async.Success)?.value

    // The poll snapshot is retired the moment a *newer* fetch lands, and not before. During polling
    // `loaded` does not change, so this leaves the live figures alone; when a refresh completes it
    // is the newest thing on the page and `polled` would otherwise shadow it indefinitely on a job
    // that has since stopped running.
    LaunchedEffect(loaded) { polled = null }
    val pollWhileRunning = loaded != null && loaded.state == MtJobState.RUNNING && !loaded.abandoned

    LaunchedEffect(slug, jobId, refreshKey, pollWhileRunning) {
        if (!pollWhileRunning) return@LaunchedEffect
        // Seeded before the first attempt, because `lastUpdated` was only ever set inside `collect`
        // — so a poll that failed on its *first* try left it null, and `OfflineMarker` is gated on
        // it being non-null. The receipt then froze its figures and said nothing, which is precisely
        // the case the marker exists for. The figures on screen came from the load that ran a moment
        // ago, so that is the time they are current as of.
        if (lastUpdated == null) lastUpdated = nowUtcClock()
        while (true) {
            try {
                ApiClient.pollJob(slug, jobId, POLL_INTERVAL_MS).collect { update ->
                    polled = update
                    lastUpdated = nowUtcClock()
                    reconnecting = false
                }
                // The flow ends only on a terminal state, which is where polling is meant to stop.
                return@LaunchedEffect
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (offline: Throwable) {
                // Keep the snapshot, say so above the fold, and try again. Not an error state: the
                // figures on screen are still the figures the server last sent.
                reconnecting = true
                delay(POLL_INTERVAL_MS)
            }
        }
    }

    val toasts = rememberToasts()
    val scope = rememberCoroutineScope()
    var acting by remember(slug, jobId) { mutableStateOf(false) }
    // Stopping and retrying a run both spend, which is role ≥ ADMIN on the server — exactly what
    // STRUCTURE captures. Null for anybody else, and every control that needs it is then absent
    // rather than disabled.
    val spendClaim = ProjectActionClaim.capture(
        liveProjectRoute(slug),
        Session.organizations,
        ProjectPrivilege.STRUCTURE,
    )

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.projects.slug.data.jobs.job_id.translation_run", "Translation run"),
            t("app.projects.slug.data.jobs.job_id.what_it_did_and_what_it", "What it did, and what it cost."),
            // §2.1: the job id is the only identifier this surface may show, and the whole of what
            // support needs to find a run. Three alert branches tell the reader to copy it; until
            // this field existed there was nothing on the page to copy.
            meta = { JobIdField(jobId) },
            actions = {
                val current = (state as? Async.Success)?.value
                ReceiptPrimaryAction(
                    slug = slug,
                    job = current,
                    claim = spendClaim,
                    busy = acting,
                    onCancel = { job ->
                        val claim = spendClaim ?: return@ReceiptPrimaryAction
                        scope.launch {
                            acting = true
                            try {
                                requestProjectAction(
                                    claim = claim,
                                    currentRoute = { liveProjectRoute(slug) },
                                    currentMemberships = { Session.organizations },
                                ) { ApiClient.cancelMtJob(slug, job.id) }
                                toasts.show(
                                    tr("app.projects.slug.data.jobs.job_id.run_stopped", "Run stopped"),
                                    LToastVariant.NEUTRAL,
                                )
                                refreshKey++
                            } catch (cancelled: CancellationException) {
                                throw cancelled
                            } catch (failure: Throwable) {
                                toasts.show(
                                    failure.receiptMessage(
                                        tr(
                                            "app.projects.slug.data.jobs.job_id.couldnt_stop_this_run",
                                            "Couldn't stop this run. It may have finished on its own.",
                                        ),
                                    ),
                                    LToastVariant.DANGER,
                                    TOAST_UNTIL_DISMISSED,
                                )
                            } finally {
                                acting = false
                            }
                        }
                    },
                    onRetry = { job ->
                        val claim = spendClaim ?: return@ReceiptPrimaryAction
                        scope.launch {
                            acting = true
                            try {
                                val started = requestProjectAction(
                                    claim = claim,
                                    currentRoute = { liveProjectRoute(slug) },
                                    currentMemberships = { Session.organizations },
                                ) { ApiClient.retryMtJob(slug, job.id) }
                                ctx.router.tryRoutingTo("/app/projects/$slug/data/jobs/$started")
                            } catch (cancelled: CancellationException) {
                                throw cancelled
                            } catch (failure: Throwable) {
                                // `job_not_retryable` means the row records nothing about what that
                                // run covered — an honest sentence, not a red failure: the way
                                // forward is the pre-translate screen, which is one press away.
                                toasts.show(
                                    failure.retryRefusal(),
                                    LToastVariant.WARNING,
                                    TOAST_UNTIL_DISMISSED,
                                )
                            } finally {
                                acting = false
                            }
                        }
                    },
                    onStartAnother = {
                        ctx.router.tryRoutingTo(
                            "/app/projects/$slug/pretranslate" + (current?.let { job ->
                                "?provider=${job.provider.id}" +
                                    "&locales=${job.locales.joinToString(",")}" +
                                    "&onlyEmpty=${job.onlyEmpty}"
                            } ?: ""),
                        )
                    },
                )
                LIconAction(
                    label = t("app.projects.slug.data.jobs.job_id.refresh_this_receipt", "Refresh this receipt"),
                    onClick = { refreshKey++ },
                    // Icon-only, so `aria-busy` is the whole of how a listener learns the press did
                    // anything; a glyph swapped for a spinner says nothing to anybody not looking.
                    // `loading` rather than `enabled = false`: a disabled element drops focus to
                    // `<body>` on the press that disabled it, and it already refuses the click.
                    loading = receipt.refreshing,
                    icon = { LRefreshIcon() },
                )
            },
        )

        // Beside the figures rather than instead of them: the receipt on screen is still the last
        // one the server sent, and blanking it because a refresh failed loses numbers somebody may
        // be part-way through reconciling. Dense, because it is a line about the fetch and not
        // about the run; live, because nothing else on the page changed when the refresh failed.
        receipt.refreshError?.let { error ->
            LAlert(
                title = t(
                    "app.projects.slug.data.jobs.job_id.couldnt_refresh_this_receipt",
                    "Couldn't refresh this receipt",
                ),
                description = tf(
                    "app.projects.slug.data.jobs.job_id.showing_the_last_receipt_that_loaded",
                    "Showing the last receipt that loaded. {reason}",
                    "reason" to error.readable(),
                ),
                variant = LAlertVariant.WARNING,
                live = true,
                dense = true,
            )
        }

        when (state) {
            Async.Loading -> ReceiptSkeleton()
            is Async.Failure -> AsyncFailureState(state, onRetry = { refreshKey++ })
            is Async.Success -> {
                val job = polled ?: state.value
                val staleAt = lastUpdated
                if (reconnecting && staleAt != null) OfflineMarker(staleAt)

                // The receipt rewrites itself on every poll while a run is in flight, and until now
                // said nothing when it stopped. A reader who cannot see the page had no way to know
                // the run they were waiting on had finished, or failed.
                Span(
                    LVisuallyHiddenStyle.toModifier().toAttrs {
                        attr("aria-live", "polite")
                        attr("aria-atomic", "true")
                        attr("data-job-status-announcement", "")
                    },
                ) {
                    Text(jobStateAnnouncement(job))
                }
                ReceiptAlert(job)
                WhatItDid(job)
                WhatItCost(job)
                WhereToCheckThis(job)
                ReceiptLinks(slug)
            }
        }
    }
    ToastHost(toasts)
}

/**
 * ⑤ The one action this receipt offers, and which one it is depends on how the run ended.
 *
 * **One primary action per screen.** A finished run's next step is another run; a failed run's is
 * the same run again — the server re-resolves what it covered, so "Try again" here is not an empty
 * form. While it is still going the only useful thing is to stop it, and that is not a primary
 * action, so it is drawn `SECONDARY`.
 *
 * **Absent below admin**, matching the routes it calls: a translator who followed the link is
 * refused, and offering it teaches them the product is broken rather than that the action is not
 * theirs.
 */
@Composable
private fun ReceiptPrimaryAction(
    slug: String,
    job: MtJobDto?,
    claim: ProjectActionClaim?,
    busy: Boolean,
    onCancel: (MtJobDto) -> Unit,
    onRetry: (MtJobDto) -> Unit,
    onStartAnother: () -> Unit,
) {
    if (claim == null) return
    when {
        job == null -> Unit
        job.state == MtJobState.RUNNING && !job.abandoned -> LButton(
            t("app.projects.slug.data.jobs.job_id.stop_this_run", "Stop this run"),
            { onCancel(job) },
            variant = LButtonVariant.SECONDARY,
            enabled = !busy,
            loading = busy,
            leadingIcon = { LPauseIcon() },
        )
        job.state == MtJobState.FAILED -> LButton(
            t("app.projects.slug.data.jobs.job_id.try_again", "Try again"),
            { onRetry(job) },
            enabled = !busy,
            loading = busy,
            leadingIcon = { LRefreshIcon() },
        )
        else -> LButton(
            t("app.projects.slug.data.jobs.job_id.start_another", "Start another"),
            onStartAnother,
            enabled = !busy,
            leadingIcon = { LSparkleIcon() },
        )
    }
}

/** What the server said when it refused a retry, in this page's words. */
private fun Throwable.retryRefusal(): String = when ((this as? ApiException)?.code) {
    "job_not_retryable" -> tr(
        "app.projects.slug.data.jobs.job_id.we_didnt_record_what_this_run",
        "We didn't record what this run covered, so it can't be repeated exactly. Start a new one " +
            "from the pre-translate screen.",
    )
    "job_not_failed" -> tr(
        "app.projects.slug.data.jobs.job_id.this_run_didnt_fail_so_there",
        "This run didn't fail, so there is nothing to repeat.",
    )
    "job_already_running" -> tr(
        "app.projects.slug.data.jobs.job_id.a_run_is_already_going_for",
        "A run is already going for these languages.",
    )
    else -> receiptMessage(
        tr(
            "app.projects.slug.data.jobs.job_id.couldnt_start_that_run_again",
            "Couldn't start that run again. Retry?",
        ),
    )
}

/** The server's own sentence when it wrote one, and this page's otherwise. Never an internal one. */
private fun Throwable.receiptMessage(fallback: String): String =
    (this as? ApiException)?.message?.takeIf(String::isNotBlank) ?: fallback

/**
 * ② The alert slot — the ladder from `run-receipt.md` §3.2, in its order.
 *
 * The page shipped with four of the nine branches, so the two commonest terminal outcomes — a clean
 * run and a run with rejections — said nothing at all where the spec says the most.
 *
 * Branch 0 (reserved more than quoted) and branch 2 (cancelled) are absent because nothing carries
 * `maxCredits` and `MtJobState` has no CANCELLED member; branch 6 (queued) likewise. Adding a branch
 * that can never fire would be worse than naming the gap here.
 *
 * `abandoned` is read before the state, because an abandoned run is FAILED-shaped and a naive ladder
 * reports the wrong refund story for it (§3.2's ordering rationale).
 */
/** What a screen reader hears when a run changes state, and nothing while it is unchanged. */
private fun jobStateAnnouncement(job: MtJobDto): String = when {
    job.abandoned -> tr(
        "app.projects.slug.data.jobs.job_id.run_abandoned",
        "This run stopped because the server restarted.",
    )
    job.state == MtJobState.FAILED -> tr("app.projects.slug.data.jobs.job_id.run_failed", "This run failed.")
    job.state == MtJobState.DONE -> tr("app.projects.slug.data.jobs.job_id.run_finished", "This run finished.")
    else -> tr("app.projects.slug.data.jobs.job_id.run_going", "This run is still going.")
}

@Composable
private fun ReceiptAlert(job: MtJobDto) {
    when {
        job.abandoned -> LAlert(
            t("app.projects.slug.data.jobs.job_id.stopped_the_server_restarted", "Stopped — the server restarted"),
            t("app.projects.slug.data.jobs.job_id.this_run_did_not_finish_and_2", "This run did not finish, and the credits for what it did not translate have been " +
                "returned. Starting it again picks up the ones that are still empty."),
            variant = LAlertVariant.INFO,
        )
        job.state == MtJobState.FAILED && job.refundedAt != null -> LAlert(
            // Deliberately INFO, not DANGER: "it failed and you were not charged" is reassuring
            // news, and a red panel makes a reader file a ticket about it (§3.3).
            t("app.projects.slug.data.jobs.job_id.it_failed_and_the_credits_came", "It failed, and the credits came back"),
            t("app.projects.slug.data.jobs.job_id.the_credits_for_what_it_did", "The credits for what it did not translate have been returned. You have not been " +
                "charged for the strings it did not deliver."),
            variant = LAlertVariant.INFO,
        )
        job.state == MtJobState.FAILED -> LAlert(
            t("app.projects.slug.data.jobs.job_id.this_run_failed", "This run failed"),
            // §3.4's "Unknown" sentence. The five specific ones are server-composed from a closed
            // set; the provider's own error text is never the body of this alert.
            t("app.projects.slug.data.jobs.job_id.this_run_stopped_and_we_dont", "This run stopped and we don't have a reason to show you. Copy the job ID above if you " +
                "email us — it's the only thing we need to find this run."),
            variant = LAlertVariant.WARNING,
        )
        job.state == MtJobState.RUNNING -> LAlert(
            t("app.projects.slug.data.jobs.job_id.still_running", "Still running"),
            t("app.projects.slug.data.jobs.job_id.credits_are_reserved_while_it_works", "Credits are reserved while it works and released for anything it does not do."),
            variant = LAlertVariant.INFO,
        )
        job.failed > 0 -> LAlert(
            tPlural(
                "app.projects.slug.data.jobs.job_id.done_but_failed_title",
                job.failed,
                "Done, but {count} string failed",
                "Done, but {count} strings failed",
            ),
            tPlural(
                "app.projects.slug.data.jobs.job_id.done_but_failed_body",
                job.failed,
                "{count} string didn't come back at all. It was left as it was.",
                "{count} strings didn't come back at all. They were left as they were.",
            ),
            variant = LAlertVariant.WARNING,
        )
        job.rejected > 0 -> LAlert(
            tPlural(
                "app.projects.slug.data.jobs.job_id.done_with_rejected_title",
                job.rejected,
                "Done, with {count} string left as it was",
                "Done, with {count} strings left as they were",
            ),
            tPlural(
                "app.projects.slug.data.jobs.job_id.done_with_rejected_body",
                job.rejected,
                "{count} string came back in a shape we would not store, so it was left untouched. " +
                    "It cost nothing. The rest were written.",
                "{count} strings came back in a shape we would not store, so they were left untouched. " +
                    "They cost nothing. The rest were written.",
            ),
            variant = LAlertVariant.INFO,
        )
        // Branch 9: a clean run gets no alert and the slot collapses to nothing.
        else -> Unit
    }
}

/**
 * ③a "What it did" — §4.
 *
 * Rows are [LKeyValueRow] compact, which is the primitive this card's geometry was written for: a
 * 200px label column and a right-aligned value, so every figure on the card stacks on one edge and
 * the differences between them are legible without arithmetic. The page previously used a private
 * left-aligned row that put nothing in a column, and folded two figures into the sentence "4,102
 * translated of 4,432 attempted" that §4.4 argues against by name.
 *
 * **The hairline bookkeeping is the caller's**, so each group clears it on its own last row.
 */
@Composable
private fun WhatItDid(job: MtJobDto) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            Eyebrow(t("app.projects.slug.data.jobs.job_id.what_it_did", "WHAT IT DID"))

            Column(Modifier.fillMaxWidth()) {
                LKeyValueRow(t("app.projects.slug.data.jobs.job_id.state", "State"), compact = true) { StateBadge(job) }
                LKeyValueRow(t("app.projects.slug.data.jobs.job_id.provider", "Provider"), job.provider.displayName, compact = true)
                LKeyValueRow(t("app.projects.slug.data.jobs.job_id.languages", "Languages"), compact = true, hairline = false) {
                    Row(Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap)) {
                        if (job.locales.isEmpty()) Text("—") else job.locales.forEach { LChip(it) }
                    }
                }
            }

            Column(Modifier.fillMaxWidth()) {
                val hasRejected = job.rejected > 0
                val hasFailed = job.failed > 0
                LKeyValueRow(t("app.projects.slug.data.jobs.job_id.strings_attempted", "Strings attempted"), tPlural(
                    "app.projects.slug.data.jobs.job_id.n_strings",
                    job.total,
                    "{count} string",
                    "{count} strings",
                ), compact = true)
                // **Not "Strings written".** `done` counts everything the workers processed,
                // rejections and failures included, and the DTO carries no written count — so the
                // spec's B2 label would be a false claim and `done - rejected - failed` would be
                // the browser computing a figure from other figures, which §1 forbids on this page.
                LKeyValueRow(
                    t("app.projects.slug.data.jobs.job_id.strings_processed", "Strings processed"),
                    tPlural(
                        "app.projects.slug.data.jobs.job_id.n_strings",
                        job.done,
                        "{count} string",
                        "{count} strings",
                    ),
                    compact = true,
                    hairline = hasRejected || hasFailed,
                )
                // Absent, never zeroed: a "Rejected 0" row invites a reader to look for a failure
                // story that does not exist.
                if (hasRejected) {
                    LKeyValueRow(t("app.projects.slug.data.jobs.job_id.rejected", "Rejected"), tPlural(
                        "app.projects.slug.data.jobs.job_id.n_strings",
                        job.rejected,
                        "{count} string",
                        "{count} strings",
                    ), compact = true, hairline = hasFailed)
                }
                if (hasFailed) {
                    LKeyValueRow(t("app.projects.slug.data.jobs.job_id.failed", "Failed"), tPlural(
                        "app.projects.slug.data.jobs.job_id.n_strings",
                        job.failed,
                        "{count} string",
                        "{count} strings",
                    ), compact = true, hairline = false)
                }
            }

            if (job.rejected > 0) {
                // One problem, not forty: the rejections in a run are almost always the same defect
                // repeated, and this sentence is why the list under it is short.
                LMuted(
                    t("app.projects.slug.data.jobs.job_id.rejected_strings_came_back_from_the_2", "Rejected strings came back from the provider in a shape we would not store — " +
                        "usually a lost placeholder or a missing plural form. They were left " +
                        "untouched rather than written wrong, and they cost nothing."),
                )
                RejectionRows(job)
            }

            Column(Modifier.fillMaxWidth()) {
                val finished = job.finishedAt
                val elapsed = finished?.let { elapsedLabel(job.createdAt, it) }
                LKeyValueRow(t("app.projects.slug.data.jobs.job_id.started", "Started"), compact = true) { Stamp(job.createdAt) }
                LKeyValueRow(t("app.projects.slug.data.jobs.job_id.finished", "Finished"), compact = true, hairline = elapsed != null) {
                    // A word, not an em-dash: an em-dash here reads as missing data rather than as
                    // "this has not happened yet" (§4.5, C2).
                    if (finished == null) StillRunning() else Stamp(finished)
                }
                if (elapsed != null) {
                    LKeyValueRow(t("app.projects.slug.data.jobs.job_id.elapsed", "Elapsed"), elapsed, compact = true, hairline = false)
                }
            }
        }
    }
}

/**
 * Which strings were refused, and why.
 *
 * **Rows, not a paragraph.** These used to be appended to the job's free-text error as
 * `keyName: reason` lines, so a page could only read them back by loading every key name in the
 * project and testing each line against all of them by prefix — which cannot separate `save` from
 * `save.confirm` at all. `MtJobDto.rejections` is the structured list, capped at
 * [dev.lingvo.shared.MAX_MT_JOB_REJECTIONS], and the gap between the cap and the count is stated
 * rather than hidden.
 */
@Composable
private fun RejectionRows(job: MtJobDto) {
    if (job.rejections.isEmpty()) return
    LTable(caption = t("app.projects.slug.data.jobs.job_id.strings_this_run_refused", "Strings this run refused")) {
        LTableHead {
            LTableRow {
                LTableHeaderCell { Text(t("app.projects.slug.data.jobs.job_id.key", "Key")) }
                LTableHeaderCell { Text(t("app.projects.slug.data.jobs.job_id.reason", "Reason")) }
            }
        }
        LTableBody {
            job.rejections.forEach { rejection ->
                LTableRow {
                    LTableCell { Span(Modifier.monoFont().toAttrs()) { Text(rejection.keyName) } }
                    LTableCell { Text(rejection.reason) }
                }
            }
        }
    }
    val uncounted = (job.rejected - job.rejections.size).coerceAtLeast(0)
    if (uncounted > 0) {
        LMuted(
            tf(
                "app.projects.slug.data.jobs.job_id.the_first_shown_are_written_down",
                "The first {shown} are written down. {rest} more were counted and not recorded — " +
                    "a run that rejects this many has one problem this many times.",
                "shown" to job.rejections.size,
                "rest" to uncounted,
            ),
        )
    }
}

/**
 * A1 — the state word.
 *
 * **Written words, never `state.name.lowercase()`** (§4.3). The lowercased enum also made
 * "Partly done" unreachable: every DONE run read "done" whether or not it had left strings behind,
 * which is the one distinction a reader of a receipt is looking for. `Stopped` is NEUTRAL rather
 * than WARNING — a refunded restart is not a problem the reader has to act on.
 *
 * The provider is a fact row on this card, not a second badge beside this one: it is not a state,
 * and two badges in a row read as two states.
 */
@Composable
private fun StateBadge(job: MtJobDto) {
    when {
        job.abandoned -> LBadge(t("app.projects.slug.data.jobs.job_id.stopped", "Stopped"), variant = LBadgeVariant.NEUTRAL)
        job.state == MtJobState.FAILED -> LBadge(t("app.projects.slug.data.jobs.job_id.failed", "Failed"), variant = LBadgeVariant.DANGER)
        job.state == MtJobState.RUNNING -> LBadge(t("app.projects.slug.data.jobs.job_id.running", "Running"), variant = LBadgeVariant.INFO)
        job.rejected > 0 || job.failed > 0 -> LBadge(t("app.projects.slug.data.jobs.job_id.partly_done", "Partly done"), variant = LBadgeVariant.WARNING)
        else -> LBadge(t("app.projects.slug.data.jobs.job_id.done", "Done"), variant = LBadgeVariant.SUCCESS)
    }
}

/**
 * ③b "What it cost" — §5, as far as the DTO reaches.
 *
 * The display face binds `reservedCredits` and nothing else, because that is the quantity the
 * estimate screen put on the button: the two screens then show the same number and can be read side
 * by side. On a run that is still going the figure is muted — nothing has settled yet.
 *
 * **Given back and Net now bind `refundedCredits`, which V46 added.** The server records what the
 * refund actually released, written from the same figure that moved the money, so this reads a fact
 * rather than deriving one — §9.3 forbids the browser from deriving either. `Net` is the single
 * subtraction allowed here, and only because both operands are server-sent integers in one unit.
 *
 * A null `refundedCredits` draws neither row rather than falling back to `reservedCredits`: null
 * means a job refunded before the column existed, and guessing there is the exact overstatement this
 * change removed. The proportion bar is still absent — it needs `netCredits` as a sent value.
 */
@Composable
private fun WhatItCost(job: MtJobDto) {
    val palette = ColorMode.current.toSitePalette()
    val words = moneyWords(job)
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            Eyebrow(t("app.projects.slug.data.jobs.job_id.what_it_cost", "WHAT IT COST"))

            Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                if (job.reservedCredits == 0) {
                    // Never a zero in a money slot to mean "free to us" — the slot becomes words
                    // (§5.6), and at 33px rather than 44 because they are words and not a figure.
                    Span(
                        Modifier
                            .displayFont()
                            .fontSize(TypeScale.Heading)
                            .fontWeight(700)
                            .lineHeight(TypeScale.DisplayLineHeight)
                            .color(palette.textPrimary)
                            .toAttrs(),
                    ) { Text(t("app.projects.slug.data.jobs.job_id.no_credits", "No credits")) }
                } else {
                    val settled = job.state != MtJobState.RUNNING
                    Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.Bottom) {
                        Span(
                            Modifier
                                .displayFont()
                                .fontSize(TypeScale.Display)
                                .fontWeight(700)
                                .lineHeight(TypeScale.DisplayLineHeight)
                                .tabularNumbers()
                                .color(if (settled) palette.textPrimary else palette.textMuted)
                                .toAttrs(),
                        ) { Text(formatCount(job.reservedCredits)) }
                        Span(
                            Modifier.fontSize(TypeScale.Body).color(palette.textSecondary).toAttrs(),
                        ) { Text(t("app.projects.slug.data.jobs.job_id.credits", "credits")) }
                    }
                    Span(
                        Modifier.fontSize(TypeScale.BodySmall).color(palette.textSecondary).toAttrs(),
                    ) {
                        // The muting says "nothing has settled", not "this may grow": the
                        // reservation is taken once, for the whole job, at the start.
                        Text(
                            if (settled) {
                                t("app.projects.slug.data.jobs.job_id.reserved", "reserved")
                            } else {
                                t("app.projects.slug.data.jobs.job_id.reserved_up_front_for_the_whole", "reserved up front for the whole run")
                            },
                        )
                    }
                }
            }

            // Only when the server said so. A null is a job refunded before V46 existed, and
            // printing the reservation in its place is the overstatement this row was added to end.
            job.refundedCredits?.let { refunded ->
                Column(Modifier.fillMaxWidth()) {
                    LKeyValueRow(t("app.projects.slug.data.jobs.job_id.given_back", "Given back"), formatCount(refunded))
                    LKeyValueRow(t("app.projects.slug.data.jobs.job_id.net", "Net"), formatCount((job.reservedCredits - refunded).coerceAtLeast(0)))
                }
            }

            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                LMuted(words.keySourceEcho)
                LMuted(words.situation)
                if (words.standingNote) {
                    LMuted(
                        t("app.projects.slug.data.jobs.job_id.credits_are_held_for_the_whole_2", "Credits are held for the whole run when it starts, so a run can't stop " +
                            "half way for want of credit."),
                    )
                }
            }
        }
    }
}

/** The three sentences of §5.5, and whether the standing note would repeat one of them. */
private class MoneyWords(
    val keySourceEcho: String,
    val situation: String,
    val standingNote: Boolean,
)

private fun moneyWords(job: MtJobDto): MoneyWords {
    val refundedOn = job.refundedAt
    if (job.reservedCredits == 0) {
        return MoneyWords(
            keySourceEcho = tr(
                "app.projects.slug.data.jobs.job_id.this_run_used_your_own_provider",
                "This run used your own provider key — no credits.",
            ),
            situation = tr(
                "app.projects.slug.data.jobs.job_id.this_project_pays_its_own_provider",
                "This project pays its own provider directly. Credits are Lingvo's key only, so " +
                    "there is nothing here to reconcile.",
            ),
            standingNote = false,
        )
    }
    val echo = tr(
        "app.projects.slug.data.jobs.job_id.lingvos_key_this_is_what_spent",
        "Lingvo's key — this is what spent credits.",
    )
    return when {
        job.cancelled -> MoneyWords(
            echo,
            tr(
                "app.projects.slug.data.jobs.job_id.you_stopped_this_run_the_credits",
                "You stopped this run. The credits for what it had not translated were released; " +
                    "the strings it had already written stay, and stay paid for.",
            ),
            standingNote = true,
        )
        refundedOn != null -> MoneyWords(
            echo,
            // No amount, on purpose: see the file KDoc. The date is a fact the server did send.
            trf(
                "app.projects.slug.data.jobs.job_id.the_credits_for_what_this_run",
                "The credits for what this run did not translate were returned on {date}.",
                "date" to refundedOn.take(10),
            ),
            standingNote = true,
        )
        job.state == MtJobState.FAILED -> MoneyWords(
            echo,
            trf(
                "app.projects.slug.data.jobs.job_id.we_have_not_returned_these_credits",
                "We have not returned these credits yet. If this doesn't clear on its own, copy " +
                    "the job ID above and email {email}.",
                "email" to SUPPORT_EMAIL,
            ),
            standingNote = true,
        )
        job.state == MtJobState.RUNNING -> MoneyWords(
            echo,
            tr(
                "app.projects.slug.data.jobs.job_id.credits_are_held_when_a_run",
                "Credits are held when a run starts. Anything it does not translate is released " +
                    "when it finishes.",
            ),
            // Printing the standing note here puts the same sentence on the card twice.
            standingNote = false,
        )
        else -> MoneyWords(
            echo,
            tr(
                "app.projects.slug.data.jobs.job_id.credits_are_held_when_a_run_2",
                "Credits are held when a run starts and released for anything it does not translate.",
            ),
            standingNote = false,
        )
    }
}

/**
 * ③c "Where to check this" — §6.
 *
 * A receipt that cannot be traced onward is not a receipt: this card is the reconciliation path from
 * one run to the month's usage and to the ledger rows that carry the reservation and its release.
 *
 * **Absent for translators and viewers, not disabled.** `/app/settings/usage` is admin-and-up, and
 * an interface that offers what the server refuses reads as a broken product. Absent when the run
 * spent nothing, too — there is no ledger row to go and look at.
 *
 * The body names no figure. §6's literal quotes the net, which is a server field this page does not
 * have; a number invented here would be exactly the contradiction the card exists to prevent.
 */
@Composable
private fun WhereToCheckThis(job: MtJobDto) {
    if (job.reservedCredits == 0) return
    if (WorkspaceSelection.current()?.role?.atLeast(Role.ADMIN) != true) return
    val month = usageMonthOf(job.createdAt)

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            Eyebrow(t("app.projects.slug.data.jobs.job_id.where_to_check_this", "WHERE TO CHECK THIS"))
            LMuted(
                if (job.state == MtJobState.RUNNING) {
                    t("app.projects.slug.data.jobs.job_id.these_credits_are_reserved_whatever_isnt", "These credits are reserved. Whatever isn't used is released when the run finishes, and both movements show in the credit ledger.")
                } else {
                    t("app.projects.slug.data.jobs.job_id.this_runs_credits_are_part_of", "This run's credits are part of the month's total on Usage. The credit ledger shows the reservation and the release as two separate rows.")
                },
            )
            Column(Modifier.fillMaxWidth()) {
                TallLink(
                    if (month == null) {
                        t("app.projects.slug.data.jobs.job_id.open_usage", "Open usage")
                    } else {
                        trf(
                            "app.projects.slug.data.jobs.job_id.open_usage_for_month",
                            "Open usage for {month}",
                            "month" to month.label,
                        )
                    },
                    if (month == null) "/app/settings/usage" else "/app/settings/usage?from=${month.from}&to=${month.to}",
                )
                TallLink(
                    t("app.projects.slug.data.jobs.job_id.open_the_credit_ledger", "Open the credit ledger"),
                    "/app/settings/usage/events",
                )
            }
        }
    }
}

/**
 * The way onward — §8.1, now that the acting controls live in the header.
 *
 * Links, not buttons: everything here navigates, and the one thing on this page that *does*
 * something is the primary action beside the title.
 */
@Composable
private fun ReceiptLinks(slug: String) {
    Row(Modifier.gap(Space.XL).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
        TallLink(t("app.projects.slug.data.jobs.job_id.all_runs", "All runs"), "/app/projects/$slug/data/jobs")
        TallLink(t("app.projects.slug.data.jobs.job_id.project_data", "Project data"), "/app/projects/$slug/data")
    }
}

/**
 * The loading composition — §10.
 *
 * Not `LoadingState(rows = 4)`, which the spec names as the thing not to do here: it draws a 42%
 * text bar over four 56px blocks, a shape that matches nothing on this page, so every row moves when
 * the data lands. These skeletons are the real row heights, and the alert slot reserves nothing —
 * a clean run has no alert, and a promised region that evaporates reads as a bug where a region that
 * arrives reads as arrival.
 */
@Composable
private fun ReceiptSkeleton() {
    // **One live region, not one per bar.** Every bar here carried the same "Loading the run"
    // label, and each `LSkeleton` is its own `role="status"` — with nine repeated rows inside the
    // first card that was the same sentence announced more than twenty times before the page had
    // said anything. `LoadingState` solved this for the generic case; this skeleton draws its own
    // bars because the receipt's row heights matter, so it has to solve it too.
    Span(LVisuallyHiddenStyle.toModifier().toAttrs { attr("role", "status") }) {
        Text(t("app.projects.slug.data.jobs.job_id.loading_the_run", "Loading the run"))
    }
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            LSkeleton(width = 120.px, height = 16.px, shape = LSkeletonShape.TEXT, label = null)
            Column(Modifier.fillMaxWidth()) {
                repeat(9) { SkeletonRow() }
            }
        }
    }
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            LSkeleton(width = 120.px, height = 16.px, shape = LSkeletonShape.TEXT, label = null)
            LSkeleton(width = 160.px, height = 48.px, shape = LSkeletonShape.TEXT, label = null)
            LSkeleton(width = 90.px, height = 20.px, shape = LSkeletonShape.TEXT, label = null)
            LSkeleton(width = 100.percent, height = 40.px, shape = LSkeletonShape.TEXT, label = null)
        }
    }
    Row(Modifier.gap(Space.XL)) {
        LButton(t("app.projects.slug.data.jobs.job_id.refresh", "Refresh"), {}, variant = LButtonVariant.GHOST, enabled = false)
    }
}

@Composable
private fun SkeletonRow() {
    Row(
        Modifier.fillMaxWidth().minHeight(48.px).gap(Space.LG),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        LSkeleton(width = 200.px, height = 12.px, shape = LSkeletonShape.TEXT, label = null)
        LSkeleton(width = 120.px, height = 14.px, shape = LSkeletonShape.TEXT, label = null)
    }
}

/**
 * §10's offline marker: the last snapshot stays on screen and one line says how old it is.
 *
 * Not a red alert and not a blank page — every figure below this line is still the figure the server
 * last sent, and a receipt that empties itself because a poll failed loses a reconciliation
 * half-done.
 */
@Composable
private fun OfflineMarker(lastUpdated: String) {
    val palette = ColorMode.current.toSitePalette()
    P(
        Modifier
            .color(palette.textMuted)
            .fontSize(TypeScale.Small)
            .lineHeight(TypeScale.BodyLineHeight)
            .margin(0.px)
            .toAttrs(),
    ) {
        Text(t("app.projects.slug.data.jobs.job_id.last_updated", "Last updated "))
        Span(Modifier.monoFont().tabularNumbers().toAttrs()) { Text(lastUpdated) }
        Text(t("app.projects.slug.data.jobs.job_id.reconnecting", ". Reconnecting."))
    }
}

/** The id, middle-elided and copyable. Absent when the route gave us nothing to copy. */
@Composable
private fun JobIdField(jobId: String) {
    if (jobId.isBlank()) return
    LCopyField(jobId, t("app.projects.slug.data.jobs.job_id.job_id_label", "job ID"))
}

@Composable
private fun Eyebrow(text: String) {
    Span(
        Modifier
            .monoFont()
            .fontSize(TypeScale.Micro)
            .letterSpacing(TypeScale.MicroTracking)
            .color(ColorMode.current.toSitePalette().textMuted)
            .toAttrs(),
    ) { Text(text) }
}

/** A timestamp in a fact row: `YYYY-MM-DD HH:MM:SS UTC`, in mono, always carrying the letters. */
@Composable
private fun Stamp(iso: String) {
    Span(Modifier.monoFont().tabularNumbers().toAttrs()) {
        Text(formatUtc(iso, LTimeStyle.STAMP))
    }
}

@Composable
private fun StillRunning() {
    Span(Modifier.color(ColorMode.current.toSitePalette().textMuted).toAttrs()) { Text(t("app.projects.slug.data.jobs.job_id.still_running", "Still running")) }
}

/** A link that carries the 40px hit area itself, because a 22px line box is below the floor. */
@Composable
private fun TallLink(label: String, path: String) {
    Link(
        path = path,
        modifier = Modifier
            .minHeight(40.px)
            .alignItems(AlignItems.Center)
            .fontSize(TypeScale.BodySmall)
            .styleModifier { property("display", "inline-flex") },
    ) { Text(label) }
}


private fun Int.pad2(): String = toString().padStart(2, '0')

private fun nowUtcClock(): String {
    val now = kotlin.js.Date()
    return "${now.getUTCHours().pad2()}:${now.getUTCMinutes().pad2()}:${now.getUTCSeconds().pad2()} UTC"
}

/**
 * `38 s` under a minute, `4 min 12 s` under an hour, `1 h 04 min` above it.
 *
 * Null rather than a guess when either stamp will not parse: an elapsed figure that does not equal
 * the difference of the two stamps printed above it is worse than no elapsed figure.
 */
private fun elapsedLabel(startIso: String, endIso: String): String? {
    val start = kotlin.js.Date(startIso).getTime()
    val end = kotlin.js.Date(endIso).getTime()
    if (start.isNaN() || end.isNaN()) return null
    val seconds = ((end - start) / 1_000.0).toLong()
    if (seconds < 0) return null
    return when {
        seconds < 60 -> "$seconds s"
        seconds < 3_600 -> "${seconds / 60} min ${(seconds % 60).toInt().pad2()} s"
        else -> "${seconds / 3_600} h ${((seconds % 3_600) / 60).toInt().pad2()} min"
    }
}

private val MONTH_NAMES = listOf(
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
)

private class UsageMonth(val label: String, val from: String, val to: String)

/** The UTC month a run started in, as the half-open range `/app/settings/usage` reads. */
private fun usageMonthOf(iso: String): UsageMonth? {
    val year = iso.take(4).toIntOrNull() ?: return null
    val month = iso.drop(5).take(2).toIntOrNull()?.takeIf { it in 1..12 } ?: return null
    val nextYear = if (month == 12) year + 1 else year
    val nextMonth = if (month == 12) 1 else month + 1
    return UsageMonth(
        label = MONTH_NAMES[month - 1],
        from = "$year-${month.pad2()}-01",
        to = "$nextYear-${nextMonth.pad2()}-01",
    )
}
